<template>
  <div
    v-if="editor"
    class="badiu-tiptap-toolbar d-flex flex-wrap align-items-center gap-1 px-2 py-1 bg-light border-bottom"
  >

    <!-- Group: History -->
    <div class="btn-group btn-group-sm" role="group" aria-label="History">
      <button
        type="button"
        class="btn btn-outline-secondary"
        title="Undo"
        @click="editor.chain().focus().undo().run()"
      >
        <i class="fas fa-undo fa-fw"></i>
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary"
        title="Redo"
        @click="editor.chain().focus().redo().run()"
      >
        <i class="fas fa-redo fa-fw"></i>
      </button>
    </div>

    <div class="vr mx-1"></div>

    <!-- Group: Style -->
    <div class="btn-group btn-group-sm" role="group" aria-label="Text style">
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('bold') ? 'active' : '']"
        title="Bold"
        @click="editor.chain().focus().toggleBold().run()"
      >
        <i class="fas fa-bold fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('italic') ? 'active' : '']"
        title="Italic"
        @click="editor.chain().focus().toggleItalic().run()"
      >
        <i class="fas fa-italic fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('underline') ? 'active' : '']"
        title="Underline"
        @click="editor.chain().focus().toggleUnderline().run()"
      >
        <i class="fas fa-underline fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('strike') ? 'active' : '']"
        title="Strikethrough"
        @click="editor.chain().focus().toggleStrike().run()"
      >
        <i class="fas fa-strikethrough fa-fw"></i>
      </button>
    </div>

    <div class="vr mx-1"></div>

    <!-- Group: Headings -->
    <select
      class="form-select form-select-sm badiu-tiptap-block-select"
      :value="currentBlockType"
      @change="onBlockChange($event)"
    >
      <option value="paragraph">Paragraph</option>
      <option value="h2">Heading 2</option>
      <option value="h3">Heading 3</option>
      <option value="h4">Heading 4</option>
    </select>

    <div class="vr mx-1"></div>

    <!-- Group: Font family -->
    <select
      class="form-select form-select-sm badiu-tiptap-font-family-select"
      :value="currentFontFamily"
      @change="onFontFamilyChange($event)"
    >
      <option value="">Default font</option>
      <option value="Arial, sans-serif">Arial</option>
      <option value="'Times New Roman', serif">Times New Roman</option>
      <option value="'Courier New', monospace">Courier New</option>
      <option value="Georgia, serif">Georgia</option>
      <option value="Verdana, sans-serif">Verdana</option>
      <option value="Tahoma, sans-serif">Tahoma</option>
      <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
    </select>

    <!-- Group: Font size -->
    <select
      class="form-select form-select-sm badiu-tiptap-font-size-select"
      :value="currentFontSize"
      @change="onFontSizeChange($event)"
    >
      <option value="">Size</option>
      <option value="10px">10</option>
      <option value="12px">12</option>
      <option value="14px">14</option>
      <option value="16px">16</option>
      <option value="18px">18</option>
      <option value="20px">20</option>
      <option value="24px">24</option>
      <option value="28px">28</option>
      <option value="32px">32</option>
      <option value="36px">36</option>
      <option value="48px">48</option>
      <option value="72px">72</option>
    </select>

    <div class="vr mx-1"></div>

    <!-- Group: Alignment -->
    <div class="btn-group btn-group-sm" role="group" aria-label="Text alignment">
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isAlign('left') ? 'active' : '']"
        title="Align left"
        @click="editor.chain().focus().setTextAlign('left').run()"
      >
        <i class="fas fa-align-left fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isAlign('center') ? 'active' : '']"
        title="Center"
        @click="editor.chain().focus().setTextAlign('center').run()"
      >
        <i class="fas fa-align-center fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isAlign('right') ? 'active' : '']"
        title="Align right"
        @click="editor.chain().focus().setTextAlign('right').run()"
      >
        <i class="fas fa-align-right fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isAlign('justify') ? 'active' : '']"
        title="Justify"
        @click="editor.chain().focus().setTextAlign('justify').run()"
      >
        <i class="fas fa-align-justify fa-fw"></i>
      </button>
    </div>

    <div class="vr mx-1"></div>

    <!-- Group: Lists -->
    <div class="btn-group btn-group-sm" role="group" aria-label="Lists">
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('bulletList') ? 'active' : '']"
        title="Bullet list"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        <i class="fas fa-list-ul fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('orderedList') ? 'active' : '']"
        title="Ordered list"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        <i class="fas fa-list-ol fa-fw"></i>
      </button>
    </div>

    <div class="vr mx-1"></div>

    <!-- Group: Insert -->
    <div class="btn-group btn-group-sm" role="group" aria-label="Insert">
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', isActive('link') ? 'active' : '']"
        title="Insert / edit link"
        @click="$emit('open-link')"
      >
        <i class="fas fa-link fa-fw"></i>
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary"
        title="Insert image"
        @click="$emit('open-image')"
      >
        <i class="fas fa-image fa-fw"></i>
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary"
        title="Insert table"
        @click="$emit('insert-table')"
      >
        <i class="fas fa-table fa-fw"></i>
      </button>
    </div>

    <!-- Group: Tools (right side) -->
    <div class="btn-group btn-group-sm ms-auto" role="group" aria-label="Tools">
      <button
        type="button"
        class="btn btn-outline-secondary"
        title="View source code"
        @click="$emit('open-source')"
      >
        <i class="fas fa-code fa-fw"></i>
      </button>
      <button
        type="button"
        :class="['btn', 'btn-outline-secondary', fullscreen ? 'active' : '']"
        :title="fullscreen ? 'Restore size' : 'Expand editor'"
        @click="$emit('toggle-fullscreen')"
      >
        <i :class="['fas', 'fa-fw', fullscreen ? 'fa-compress' : 'fa-expand']"></i>
      </button>
    </div>

  </div>
</template>

<script>
/**
 * BadiuTiptapToolbar
 *
 * Isolated toolbar inspired by TinyMCE's layout:
 * logical groups separated by vertical dividers.
 *
 * Receives the editor instance via prop and emits events
 * handled by the parent component (BadiuTiptapEditor).
 */
import { computed, ref } from 'vue'

export default {
  name: "BadiuTiptapToolbar",

  props: {
    editor: {
      type: Object,
      default: null
    },

    fullscreen: {
      type: Boolean,
      default: false
    }
  },

  emits: [
    "open-link",
    "open-image",
    "open-source",
    "insert-table",
    "toggle-fullscreen"
  ],

  setup(props) {
    function isActive(name, attrs) {
      if (!props.editor) return false;
      return attrs
        ? props.editor.isActive(name, attrs)
        : props.editor.isActive(name);
    }

    function isAlign(align) {
      if (!props.editor) return false;
      return props.editor.isActive({ textAlign: align });
    }

    const currentBlockType = computed(() => {
      if (!props.editor) return "paragraph";

      if (props.editor.isActive("heading", { level: 2 })) return "h2";
      if (props.editor.isActive("heading", { level: 3 })) return "h3";
      if (props.editor.isActive("heading", { level: 4 })) return "h4";

      return "paragraph";
    });

    function onBlockChange(event) {
      const value = event.target.value;

      if (value === "paragraph") {
        props.editor.chain().focus().setParagraph().run();
      } else {
        const level = Number(value.replace("h", ""));
        props.editor.chain().focus().toggleHeading({ level }).run();
      }
    }

    const currentFontFamily = computed(() => {
      if (!props.editor) return "";
      return props.editor.getAttributes("textStyle").fontFamily || "";
    });

    const currentFontSize = computed(() => {
      if (!props.editor) return "";
      return props.editor.getAttributes("textStyle").fontSize || "";
    });

    function onFontFamilyChange(event) {
      const value = event.target.value;

      if (value) {
        props.editor.chain().focus().setMark("textStyle", { fontFamily: value }).run();
      } else {
        props.editor.chain().focus().setMark("textStyle", { fontFamily: null }).run();
      }
    }

    function onFontSizeChange(event) {
      const value = event.target.value;

      if (value) {
        props.editor.chain().focus().setMark("textStyle", { fontSize: value }).run();
      } else {
        props.editor.chain().focus().setMark("textStyle", { fontSize: null }).run();
      }
    }

    return {
      isActive,
      isAlign,
      currentBlockType,
      onBlockChange,
      currentFontFamily,
      currentFontSize,
      onFontFamilyChange,
      onFontSizeChange
    };
  }
};
</script>

<style>
/* Fixed widths for toolbar selects so they don't grow with the toolbar. */
.badiu-tiptap-block-select {
  width: 110px;
}

.badiu-tiptap-font-family-select {
  width: 150px;
}

.badiu-tiptap-font-size-select {
  width: 70px;
}

/* Active formatting buttons use a subtle blue highlight. */
.badiu-tiptap-toolbar .btn-outline-secondary.active {
  background-color: #dde5f0;
  border-color:     #9ec5fe;
  color:            #084298;
}
</style>
